export const components = {
    Progress: {
        baseStyle: {
            track: {
                bg: '#EBEBEB',
                borderRadius: '99px',
            },
            filledTrack: {
                bg: '#215AA8',
                borderRadius: '99px',
            },
        },
    },

    Checkbox: {
        baseStyle: {
            borderColor: '#2A3D4A',
        },
        defaultProps: {
            colorScheme: 'sagitarius',
        },
    },

    Input: {
        baseStyle: {},
        variants: {
            outline: {
                field: {
                    fontSize: '14px',
                    borderRadius: '4px',
                    border: '1.2px solid',
                    borderColor: 'neutral.200',
                    _placeholder: {
                        color: 'neutral.400',
                    },
                    _disabled: {
                        color: 'neutral.400',
                        bg: 'semantic.200',
                        cursor: 'not-allowed',
                        opacity: 1,
                    },
                },
            },
        },
    },

    Button: {
        baseStyle: {
            borderRadius: '6px',
            gap: '12px',
            letterSpacing: '0.5%',
            p: '12px 12px',
        },
        defaultProps: {
            colorScheme: 'paragon',
        },
        size: {
            sm: {
                borderRadius: '4px',
            },
        },
        variants: {
            outline: (props) => {
                const { colorScheme: c } = props;

                return {
                    fontWeight: '400',
                    color: `${c}.500`,
                    border: '1px solid',
                    borderColor: `${c}.500`,
                    _hover: {
                        color: `${c}.600`,
                        bg: `${c}.100`,
                    },
                    _active: {
                        color: `${c}.700`,
                    },
                };
            },
            secondary: {
                p: '10px',
                bg: 'semantic.100',
                color: 'white',
                border: '1px solid',
                borderRadius: '4px',
                borderColor: 'neutral.200',
                _hover: {
                    bg: 'semantic.200',
                    borderColor: 'neutral.200 !important',
                },
            },
            unstyled: (props) => {
                const { colorScheme: c } = props;

                return {
                    display: 'flex',
                    gap: '12px',
                    fontWeight: '400',
                    color: `${c}.500`,
                    _hover: {
                        color: `${c}.600`,
                    },
                    _active: {
                        color: `${c}.700`,
                    },
                };
            },
            text: {
                color: 'sagitarius.500',
                fontWeight: '600',
                fontSize: '14px',
            },
        },
    },

    Text: {
        baseStyle: {
            color: 'semantic.500',
        },
    },

    Alert: {
        baseStyle: {
            container: {
                borderRadius: '8px',
                fontWeight: 'medium',
            },
        },
        variants: {
            subtle: (props) => {
                const bgMap = {
                    success: 'success.100',
                    error: 'danger.100',
                    warning: '#FFF4E5',
                    info: '#E6F0FB',
                };
                return {
                    container: {
                        bg: bgMap[props.status],
                        color: 'semantic.500',
                    },
                };
            },

            'left-accent': (props) => {
                const bgMap = {
                    success: 'success.100',
                    error: 'danger.100',
                    warning: 'warning.100',
                    info: 'info.100',
                };

                const accentMap = {
                    success: 'success.600',
                    error: 'danger.600',
                    warning: 'warning.600',
                    info: 'info.600',
                };

                return {
                    container: {
                        borderRadius: '4px',
                        bg: bgMap[props.status],
                        borderInlineStartColor: accentMap[props.status],
                        borderInlineStartWidth: '4px',
                        fontSize: '14px',
                    },
                };
            },

            border: (props) => {
                const bgMap = {
                    success: 'success.100',
                    error: 'danger.100',
                    warning: 'warning.100',
                    info: 'info.100',
                };

                const accentMap = {
                    success: 'success.600',
                    error: 'danger.600',
                    warning: 'warning.600',
                    info: 'info.600',
                };

                return {
                    container: {
                        borderRadius: '4px',
                        bg: bgMap[props.status],
                        border: `1px solid`,
                        borderColor: accentMap[props.status],
                        fontSize: '14px',
                    },
                };
            },
        },
    },

    Tooltip: {
        baseStyle: {
            bg: 'semantic.900',
            color: 'semantic.100',
            borderRadius: '4px',
            padding: '8px 16px',
            fontWeight: 400,
            fontSize: '14px',
            '--popper-arrow-bg': 'colors.semantic.900',
        },
    },

    Spinner: {
        baseStyle: {
            width: '20px',
            height: '20px',
        },
    },

    Tag: {
        baseStyle: {
            container: {
                borderRadius: '4px',
                p: '4px 6px !important',
            },
        },
        variants: Object.fromEntries(
            Object.entries({
                draft: { bg: 'neutral.100', color: 'neutral.800' },
                'waiting for approval': { bg: 'warning.100', color: 'warning.800' },
                planned: { bg: 'success.100', color: 'success.800' },
                rejected: { bg: 'danger.100', color: 'danger.800' },
                active: { bg: 'info.100', color: 'info.800' },
                'ready to claim': { bg: 'success.100', color: 'success.800' },
                done: { bg: 'success.100', color: 'success.800' },
            }).map(([status, colors]) => [status, { container: colors }]),
        ),
        defaultProps: {
            variant: 'planned',
        },
    },

    Popover: {
        baseStyle: {
            content: {
                w: 'fit-content',
                alignItems: 'start',
                borderRadius: '8px',
                border: 'none',
                overflow: 'hidden',
                boxShadow: '0px 1px 3px 0px #0000001A',
            },
            header: {
                fontWeight: 'bold',
            },
            body: {
                p: 0,
                transition: '.3s all ease',

                _hover: {
                    bg: 'semantic.200',
                },
            },
        },
    },

    Tabs: {
        baseStyle: {
            tab: {
                fontSize: '16px',
                fontWeight: 600,
                _selected: {
                    color: 'paragon.500',
                    borderColor: 'paragon.500',
                },
                _notSelected: {
                    color: 'semantic.100',
                },
            },

            tabpanel: {
                p: '24px 0',
            },
        },
        variants: {
            line: {
                tab: {
                    borderColor: 'neutral.400',
                    color: 'neutral.400',
                    _selected: {
                        color: 'paragon.500',
                        borderColor: 'paragon.500',
                    },
                    _notSelected: {
                        color: 'semantic.100',
                    },
                },
            },
            'sagi-rounded': {
                tab: {
                    border: '1px solid',
                    px: '32px',
                    borderRadius: 'full',
                    color: 'neutral.600',
                    fontWeight: 500,
                    _selected: {
                        color: 'sagitarius.500',
                        fontWeight: 600,
                        border: 0,
                        bg: 'sagitarius.100',
                    },
                },
                tablist: {
                    gap: '12px',
                },
            },
        },
        defaultProps: {
            variant: 'line',
            colorScheme: 'paragon',
        },
    },
};
